/**
 * Contacts hook that reads from DataHub AppSheet Shared Contacts collection
 *
 * This hook reads contact data from the appsheet_shared_contacts collection
 * which is synced via the DataHub AppSheet connector.
 *
 * Contacts are linked to generators and service logs via comma-separated
 * AppSheet ID strings in the 'Generators' and 'Related Service Logs' fields.
 */
import { parseAppSheetBoolean } from '../lib/appsheet/yesNo';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { useOrganization } from '../contexts/OrganizationContext';
import { isSoftDeleted } from '../lib/appsheet/softDelete';
import { DataHubSharedContactSchema, validateDataHubData } from '../types/datahub-schemas';

// =============================================================================
// Types
// =============================================================================

/**
 * Raw contact data from AppSheet sync
 */
export interface DataHubSharedContact {
  hubDocId: string;
  appId: string;
  appSheetId: string;
  syncedAt: Timestamp;

  // Identifiers
  rowId: string;
  contactId: string;

  // Contact info
  firstName: string;
  lastName: string;
  salutation: string;
  computedName: string;
  company: string;

  // Contact details
  primaryPhone: string;
  otherPhones: string[];
  primaryEmail: string;
  otherEmails: string[];

  // Classification
  role: string;
  isCommercial: boolean;
  branch: string;

  // Related records (parsed from comma-separated strings)
  generatorIds: string[];
  relatedGeneratorIds: string[];
  serviceLogIds: string[];

  // Display
  contactLabel: string;
  notes: string;

  // Metadata
  createdBy: string;
  lastModified: string;
}

/**
 * Normalized contact for UI consumption
 */
export interface AppSheetContact {
  contactId: string;
  appSheetId: string;

  // Name
  firstName: string;
  lastName: string;
  fullName: string;
  salutation: string;

  // Company/customer
  company: string;

  // Contact methods
  primaryPhone: string;
  otherPhones: string[];
  primaryEmail: string;
  otherEmails: string[];
  allPhones: string[];
  allEmails: string[];

  // Classification
  role: string;
  isCommercial: boolean;
  branch: string;

  // Relationships
  linkedGeneratorIds: string[];
  linkedServiceLogIds: string[];

  // Display helpers
  displayLabel: string;
  notes: string;

  // Metadata
  source: 'appsheet';
  lastModified: string;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Parse comma-space separated string into array of IDs
 */
function parseCommaSeparatedIds(value: string | undefined): string[] {
  if (!value || value.trim() === '') return [];
  return value
    .split(/\s*,\s*/)
    .map(id => id.trim())
    .filter(id => id.length > 0);
}

/**
 * Map raw DataHub contact to normalized AppSheetContact
 */
function mapToAppSheetContact(raw: DataHubSharedContact): AppSheetContact {
  const fullName = raw.computedName?.trim() ||
    [raw.firstName, raw.lastName].filter(Boolean).join(' ') ||
    raw.company ||
    'Unknown Contact';

  const allPhones = [raw.primaryPhone, ...raw.otherPhones].filter(Boolean);
  const allEmails = [raw.primaryEmail, ...raw.otherEmails].filter(Boolean);

  // Combine both generator ID fields and deduplicate
  const linkedGeneratorIds = Array.from(
    new Set([...raw.generatorIds, ...raw.relatedGeneratorIds])
  );

  return {
    contactId: raw.contactId,
    appSheetId: raw.appSheetId,

    firstName: raw.firstName,
    lastName: raw.lastName,
    fullName,
    salutation: raw.salutation,

    company: raw.company,

    primaryPhone: raw.primaryPhone,
    otherPhones: raw.otherPhones,
    primaryEmail: raw.primaryEmail,
    otherEmails: raw.otherEmails,
    allPhones,
    allEmails,

    role: raw.role,
    isCommercial: raw.isCommercial,
    branch: raw.branch,

    linkedGeneratorIds,
    linkedServiceLogIds: raw.serviceLogIds,

    displayLabel: raw.contactLabel || fullName,
    notes: raw.notes,

    source: 'appsheet',
    lastModified: raw.lastModified,
  };
}

/**
 * Map raw Firestore document to DataHubSharedContact.
 *
 * appsheet_shared_contacts stores AppSheet column names verbatim ("First Name",
 * "Primary Email", ...) — the sync field map sets no firestoreField override for
 * them. This is the ONE reader of that shape; useContacts.ts (useContactsByCompany)
 * reuses it (#4586) rather than re-typing the column names.
 */
export function mapFirestoreToDataHubContact(
  docId: string,
  data: Record<string, unknown>
): DataHubSharedContact {
  return {
    hubDocId: docId,
    appId: (data['appId'] as string) || '',
    appSheetId: (data['appSheetId'] as string) || '',
    syncedAt: data['syncedAt'] as Timestamp,

    rowId: (data['Row ID'] as string) || '',
    contactId: (data['Contact ID'] as string) || '',

    firstName: (data['First Name'] as string) || '',
    lastName: (data['Last Name'] as string) || '',
    salutation: (data['Salutation'] as string) || '',
    computedName: (data['_ComputedName'] as string) || '',
    company: (data['Company'] as string) || '',

    primaryPhone: (data['Primary Phone'] as string) || '',
    otherPhones: parseCommaSeparatedIds(data['Other Phones'] as string),
    primaryEmail: (data['Primary Email'] as string) || '',
    otherEmails: parseCommaSeparatedIds(data['Other Emails'] as string),

    role: (data['Role'] as string) || '',
    isCommercial: parseAppSheetBoolean(data['Commercial Contact']),
    branch: (data['Branch'] as string) || '',

    generatorIds: parseCommaSeparatedIds(data['Generators'] as string),
    relatedGeneratorIds: parseCommaSeparatedIds(data['Related Generators'] as string),
    serviceLogIds: parseCommaSeparatedIds(data['Related Service Logs'] as string),

    contactLabel: (data['Contact Label'] as string) || '',
    notes: (data['Notes'] as string) || '',

    createdBy: (data['Created By'] as string) || '',
    lastModified: (data['Last Modified'] as string) || '',
  };
}

// =============================================================================
// Query Keys
// =============================================================================

export const dataHubContactKeys = {
  all: ['datahub', 'contacts'] as const,
  list: () => [...dataHubContactKeys.all, 'list'] as const,
  byCompany: (company: string) => [...dataHubContactKeys.all, 'company', company] as const,
  byGenerator: (generatorId: string) => [...dataHubContactKeys.all, 'generator', generatorId] as const,
  search: (term: string) => [...dataHubContactKeys.all, 'search', term] as const,
};

// =============================================================================
// Hooks
// =============================================================================

/** Every org contact (the queryFn of useDataHubContacts; also used by the tech offline prefetch). */
export async function fetchDataHubContacts(orgId: string | undefined): Promise<AppSheetContact[]> {
  if (!orgId) return [];
  // DASH-PERM: firestore.rules keeps a strict allow-list on this
  // collection (no tolerant OR), which denies an unconstrained list
  // outright. A matching where('organizationId') is required.
  const q = query(
    collection(db, COLLECTIONS.APPSHEET_SHARED_CONTACTS),
    where('organizationId', '==', orgId),
    orderBy('Company', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.filter((d) => !isSoftDeleted(d.data())).map((docSnapshot) => {
    const rawData = docSnapshot.data();

    // Validate with schema (logs warnings but doesn't fail)
    validateDataHubData(
      DataHubSharedContactSchema,
      rawData,
      docSnapshot.id,
      'SharedContact'
    );

    const dataHubContact = mapFirestoreToDataHubContact(docSnapshot.id, rawData);
    return mapToAppSheetContact(dataHubContact);
  });
}

/**
 * Fetch all contacts from DataHub appsheet_shared_contacts collection
 */
export function useDataHubContacts() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId;

  return useQuery({
    queryKey: [...dataHubContactKeys.list(), orgId],
    queryFn: () => fetchDataHubContacts(orgId),
    enabled: !!orgId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Search contacts by company name (customer)
 * Returns contacts where company name matches (case-insensitive)
 */
export function useDataHubContactsByCompany(companyName?: string) {
  const { data: allContacts, ...rest } = useDataHubContacts();

  const filteredContacts = useMemo(() => {
    if (!companyName || !allContacts) return [];

    const searchLower = companyName.toLowerCase().trim();
    return allContacts.filter(contact =>
      contact.company.toLowerCase().includes(searchLower)
    );
  }, [allContacts, companyName]);

  return { data: filteredContacts, ...rest };
}

/**
 * Search contacts linked to a specific generator
 * Checks the linkedGeneratorIds array for matches
 */
export function useDataHubContactsByGenerator(generatorId?: string) {
  const { data: allContacts, ...rest } = useDataHubContacts();

  const filteredContacts = useMemo(() => {
    if (!generatorId || !allContacts) return [];

    return allContacts.filter(contact =>
      contact.linkedGeneratorIds.includes(generatorId)
    );
  }, [allContacts, generatorId]);

  return { data: filteredContacts, ...rest };
}

/**
 * Search contacts by multiple criteria
 * Useful for finding contacts related to a customer/equipment in quote wizard
 *
 * @param options.companyName - Customer/company name to match
 * @param options.generatorIds - Array of generator IDs to match
 * @param options.searchTerm - General search term (name, email, phone)
 */
export function useDataHubContactsSearch(options: {
  companyName?: string;
  generatorIds?: string[];
  searchTerm?: string;
}) {
  const { data: allContacts, ...rest } = useDataHubContacts();

  const filteredContacts = useMemo(() => {
    if (!allContacts) return [];

    let results = [...allContacts];

    // Filter by company name
    if (options.companyName) {
      const companyLower = options.companyName.toLowerCase().trim();
      results = results.filter(contact =>
        contact.company.toLowerCase().includes(companyLower)
      );
    }

    // Filter by generator IDs (contact linked to any of the generators)
    if (options.generatorIds && options.generatorIds.length > 0) {
      results = results.filter(contact =>
        options.generatorIds!.some(genId =>
          contact.linkedGeneratorIds.includes(genId)
        )
      );
    }

    // Filter by search term (name, email, phone)
    if (options.searchTerm) {
      const termLower = options.searchTerm.toLowerCase().trim();
      results = results.filter(contact =>
        contact.fullName.toLowerCase().includes(termLower) ||
        contact.company.toLowerCase().includes(termLower) ||
        contact.primaryEmail.toLowerCase().includes(termLower) ||
        contact.primaryPhone.includes(termLower) ||
        contact.allEmails.some(e => e.toLowerCase().includes(termLower)) ||
        contact.allPhones.some(p => p.includes(termLower))
      );
    }

    return results;
  }, [allContacts, options.companyName, options.generatorIds, options.searchTerm]);

  return { data: filteredContacts, ...rest };
}

/**
 * Get contacts relevant to a quote context
 * Combines company name matching with equipment-linked contacts
 *
 * @param customerName - The customer/company name from the quote
 * @param equipmentIds - AppSheet generator IDs from selected equipment
 */
export function useQuoteRelevantContacts(
  customerName?: string,
  equipmentIds?: string[]
) {
  const { data: allContacts, ...rest } = useDataHubContacts();

  const relevantContacts = useMemo(() => {
    if (!allContacts) return [];
    if (!customerName && (!equipmentIds || equipmentIds.length === 0)) {
      return [];
    }

    const customerLower = customerName?.toLowerCase().trim() || '';

    // Find contacts matching by company OR linked to equipment
    const matches = allContacts.filter(contact => {
      // Match by company name
      const companyMatch = customerLower &&
        contact.company.toLowerCase().includes(customerLower);

      // Match by linked equipment
      const equipmentMatch = equipmentIds &&
        equipmentIds.length > 0 &&
        equipmentIds.some(genId => contact.linkedGeneratorIds.includes(genId));

      return companyMatch || equipmentMatch;
    });

    // Sort: equipment-linked contacts first, then by name
    return matches.sort((a, b) => {
      const aHasEquipment = equipmentIds?.some(id => a.linkedGeneratorIds.includes(id)) || false;
      const bHasEquipment = equipmentIds?.some(id => b.linkedGeneratorIds.includes(id)) || false;

      if (aHasEquipment && !bHasEquipment) return -1;
      if (!aHasEquipment && bHasEquipment) return 1;
      return a.fullName.localeCompare(b.fullName);
    });
  }, [allContacts, customerName, equipmentIds]);

  return { data: relevantContacts, ...rest };
}
